import { formatQuantity } from '../../utils/inventoryLabels';
import { isLotFixed } from '../receipts/receiptsUtils';
import { matchCodes, sameSlotCode } from '../operator/operatorCodes';
import { intentOf } from '../operator/operatorIdempotency';

/*
 * The rules of counting a receipt on the floor: where the goods land, what a
 * scanned code is, what the count still lacks, and what to say once the
 * server answered.
 */

export const RECEIVING_STEP = {
  SLOT: 'slot',
  PRODUCT: 'product',
  QUANTITY: 'quantity',
};

/** Receipts that can still be counted. */
export const OPEN_RECEIPT_STATUSES = ['pending', 'partial'];

/**
 * Whether the operator must say where the goods land before counting: the
 * receipt has no slot yet, none is locked by an earlier count, and there is
 * more than one receiving slot to choose from.
 */
export const needsSlotChoice = (receipt, slotCode) =>
  !slotCode &&
  !receipt?.slot &&
  !receipt?.slot_locked &&
  (receipt?.receiving_slots?.length ?? 0) > 1;

/** The only receiving slot, when there is one and the receipt has none yet. */
export const onlyReceivingSlotCode = (receipt) =>
  !receipt?.slot && !receipt?.slot_locked && receipt?.receiving_slots?.length === 1
    ? receipt.receiving_slots[0].code
    : null;

/** @returns {{id: number, name: string, code: string}|null} */
export const findReceivingSlot = (receipt, code) =>
  (receipt?.receiving_slots || []).find((slot) => sameSlotCode(slot.code, code)) || null;

/**
 * The lines a code can be, each with the unit it reads in.
 *
 * @returns {Array<{line: object, factor: number}>}
 */
export const matchReceiptLines = (receipt, code) =>
  (receipt?.lines || []).reduce((matches, line) => {
    const match = matchCodes(line.codes, code);
    if (match) matches.push({ line, factor: match.factor });
    return matches;
  }, []);

/**
 * What the quantity step is counting.
 *
 * @param {object} line - A receipt line.
 * @param {string} code - What was scanned: it carries the unit.
 * @param {number} factor
 */
export const draftFromLine = (line, code, factor) => ({
  code,
  lineId: line.id,
  factor,
  sku: line.product_sku,
  name: line.product_name,
  lotRequired: Boolean(line.lot_controlled),
  expiryRequired: Boolean(line.expiry_controlled),
  lockedLot: isLotFixed(line) ? line.lot : null,
  lockedExpiry: isLotFixed(line) ? line.expiry_date || null : null,
  allowsFraction: Boolean(line.allows_fractional_qty) && factor === 1,
});

/** @param {{line: object, factor: number}} match */
export const lineOption = ({ line, factor }) => ({
  id: line.id,
  factor,
  title: `${line.product_sku} — ${line.product_name}`,
  subtitle: `Línea ${line.line_no} · ${formatQuantity(line.received)} de ${formatQuantity(line.expected)} un${
    line.lot ? ` · lote ${line.lot}` : ''
  }`,
  badge: factor > 1 ? `Caja ×${factor}` : null,
});

const hasMoreThanThreeDecimals = (value) => /\.\d{4,}$/.test(String(value));

// The server takes a count of up to 11 whole digits; a barcode fired into the field has 12 or more.
const tooManyDigits = (amount) => Math.trunc(amount).toString().length > 11;

/**
 * Why a count cannot be registered yet, written for the button; or `null`.
 *
 * @param {{quantity: string|number, lot: string, expiryDate: string}} values
 * @param {object} draft - See `draftFromLine`.
 */
export const countProblem = ({ quantity, lot, expiryDate }, draft) => {
  const amount = Number(quantity);
  if (!(amount > 0)) return 'Indica cuántos llegaron';
  if (tooManyDigits(amount)) return '¿Es un código? Revisa la cantidad';
  if (!draft.allowsFraction && !Number.isInteger(amount)) return 'Se cuenta en unidades enteras';
  if (hasMoreThanThreeDecimals(quantity)) return 'Hasta 3 decimales';
  if (draft.lotRequired && !(draft.lockedLot || String(lot || '').trim())) return 'Falta el lote';
  if (draft.expiryRequired && !(draft.lockedExpiry || expiryDate)) return 'Falta el vencimiento';
  return null;
};

const roundQuantity = (value) => Math.round(value * 1000) / 1000;

/** What a line still lacks, in base units; `null` without the line. */
export const lineMissing = (line) => {
  if (!line) return null;
  if (line.missing != null) return Number(line.missing);
  return Math.max(Number(line.expected) - Number(line.received), 0);
};

/**
 * How many base units a count goes over what its line still lacks, or 0.
 *
 * @param {{quantity: string|number}} values - In the unit of the scanned code.
 * @param {{factor: number, missing: ?number}} draft - The count's factor, and its line's `missing` as the
 *   receipt on screen has it. Without `missing` nothing can be said: 0.
 */
export const excessOf = ({ quantity }, { factor, missing }) => {
  const units = Number(quantity) * (Number(factor) || 1);
  if (missing == null || !Number.isFinite(Number(missing)) || !Number.isFinite(units)) return 0;
  return Math.max(roundQuantity(units - Number(missing)), 0);
};

/**
 * The lines a count goes over, as the 409 `over_receipt` lists them, read
 * against the receipt on screen; empty when it does not.
 *
 * @param {object} receipt
 * @param {{lineId: number, quantity: number, factor: number}} arg
 * @returns {Array<{line_id: number, sku: string, name: string, expected: number, received: number,
 *   missing: number, quantity: number, excess: number}>}
 */
export const countExcess = (receipt, arg) => {
  const line = (receipt?.lines || []).find((item) => item.id === arg.lineId);
  const missing = lineMissing(line);
  const excess = excessOf(arg, { factor: arg.factor, missing });
  if (!(excess > 0)) return [];
  return [
    {
      line_id: line.id,
      sku: line.product_sku,
      name: line.product_name,
      expected: Number(line.expected),
      received: Number(line.received),
      missing,
      quantity: roundQuantity(missing + excess),
      excess,
    },
  ];
};

/** A count as the operator meant it: without its key, and without the confirmation that let it go over. */
export const countIntent = (arg) => {
  const { confirmExcess, ...rest } = intentOf(arg);
  return rest;
};

/**
 * The body of `count/`. The scanned code travels with the line: the line
 * says which one, the code says in which unit the quantity is.
 *
 * @param {{code: string, lineId: number, quantity: number, lot?: string, expiryDate?: string, slotCode?: string,
 *   confirmExcess?: boolean}} arg - `confirmExcess`: the operator confirmed going over what the line lacks.
 * @param {string} key - The idempotency key.
 */
export const buildCountBody = ({ code, lineId, quantity, lot, expiryDate, slotCode, confirmExcess }, key) => {
  const scan = { code, line_id: lineId, quantity: Number(quantity) };
  if (lot) scan.lot = lot;
  if (expiryDate) scan.expiry_date = expiryDate;
  return {
    scans: [scan],
    ...(slotCode ? { slot_code: slotCode } : {}),
    ...(confirmExcess ? { confirm_excess: true } : {}),
    idempotency_key: key,
  };
};

const warningText = (warning) =>
  typeof warning === 'string' ? warning : warning?.message || warning?.detail || '';

/**
 * What to say once a count went through.
 *
 * @param {object} payload - `{receipt, replayed, capacity_warnings}`
 * @param {object} arg - The count that was sent.
 * @returns {{tone: string, message: string}}
 */
export const countMessage = (payload, arg) => {
  if (payload.replayed) return { tone: 'success', message: 'Ese conteo ya estaba registrado.' };

  const line = (payload.receipt?.lines || []).find((item) => item.id === arg.lineId);
  const units = Number(arg.quantity) * (Number(arg.factor) || 1);
  const sku = line?.product_sku ? ` ${line.product_sku}` : '';
  const progress = line ? ` · ${formatQuantity(line.received)} de ${formatQuantity(line.expected)}` : '';
  const message = `+${formatQuantity(units)} un${sku}${progress}`;

  const warnings = (payload.capacity_warnings || []).map(warningText).filter(Boolean);
  return warnings.length > 0
    ? { tone: 'warning', message: `${message}. ${warnings[0]}` }
    : { tone: 'success', message };
};

/** What to say once a receipt closed. */
export const closeMessage = (payload) => {
  const receipt = payload.receipt || {};
  if (payload.already_closed) return `${receipt.code} ya estaba cerrada.`;
  const missing = Math.max(Number(receipt.expected_units) - Number(receipt.received_units), 0);
  return missing > 0
    ? `${receipt.code} cerrada con un faltante de ${formatQuantity(missing)} un.`
    : `${receipt.code} cerrada.`;
};

/** How much is still missing, in a sentence for the confirmation. */
export const shortageMessage = (missing) => {
  const units = (missing || []).reduce((total, line) => total + Number(line.missing || 0), 0);
  return `Faltan ${formatQuantity(units)} un en ${missing.length} ${missing.length === 1 ? 'línea' : 'líneas'}.`;
};

/** The primary of a count that goes over what its line lacks: it asks first. */
export const EXCESS_REVIEW_LABEL = 'Llegaron de más: revisar';

/** How much a count goes over, in a sentence: «Sobran 9.999.960 un de POL-1.» */
export const excessMessage = (excess) => {
  const lines = excess || [];
  const units = formatQuantity(lines.reduce((total, line) => total + Number(line.excess || 0), 0));
  return lines.length === 1 ? `Sobran ${units} un de ${lines[0].sku}.` : `Sobran ${units} un en ${lines.length} líneas.`;
};

const lackingText = ({ expected, missing }) => {
  if (Number(missing) >= Number(expected)) return null;
  return Number(missing) > 0 ? `faltan ${formatQuantity(missing)}` : 'ya llegaron todas';
};

/**
 * A line a count goes over, in a sentence: «Esperabas 40 · faltan 30 · vas a
 * registrar 50 (20 de más)». What still lacks is left out when nothing came yet.
 *
 * @param {{expected: number, missing: number, excess: number}} line - As the 409 lists it.
 * @param {{quantity: number, factor: number}} [count] - What was typed, in the unit of the scanned code:
 *   a box says «2 cajas = 48 un».
 */
export const excessText = (line, count) => {
  const units = formatQuantity(roundQuantity(Number(line.missing) + Number(line.excess)));
  const amount = Number(count?.quantity);
  const registering =
    count?.factor > 1 ? `${formatQuantity(amount)} ${amount === 1 ? 'caja' : 'cajas'} = ${units} un` : units;
  return [
    `Esperabas ${formatQuantity(line.expected)}`,
    lackingText(line),
    `vas a registrar ${registering} (${formatQuantity(line.excess)} de más)`,
  ]
    .filter(Boolean)
    .join(' · ');
};

/** Whether a line a count goes over would end with more than double what was expected. */
export const excessIsLarge = (line) => {
  const expected = Number(line.expected);
  const received = line.received != null ? Number(line.received) : expected - Number(line.missing);
  return received + Number(line.missing) + Number(line.excess) > 2 * expected;
};
